import { FormData } from "@/components/AdvisorWizard";

export interface Recommendation {
  id: string;
  title: string;
  match: number;
  description: string;
  advantages: string[];
  disadvantages: string[];
  nextSteps: string;
  isRecommended?: boolean;
}

export function getRecommendations(formData: FormData): Recommendation[] {
  const recommendations: Recommendation[] = [];

  // 1. Leibrente-Empfehlung
  if (leibrenteKriterien(formData)) {
    recommendations.push(createLeibrente(formData));
  }

  // 2. Teilverkauf-Empfehlung
  if (teilverkaufKriterien(formData)) {
    recommendations.push(createTeilverkauf(formData));
  }

  // 3. Wohnungsrecht-Empfehlung
  if (wohnungsrechtKriterien(formData)) {
    recommendations.push(createWohnungsrecht(formData));
  }

  // 4. Nießbrauch-Empfehlung
  if (niessbrauchKriterien(formData)) {
    recommendations.push(createNiessbrauch(formData));
  }

  // 5. Rückmiete-Empfehlung
  if (rueckmietKriterien(formData)) {
    recommendations.push(createRueckmiete(formData));
  }

  // 6. Umkehrhypothek-Empfehlung
  if (umkehrhypothekKriterien(formData)) {
    recommendations.push(createUmkehrhypothek(formData));
  }

  // Fallback: Individuelle Beratung
  if (recommendations.length === 0) {
    recommendations.push(createIndividuelleBeratung());
  }

  // Sortierung nach Übereinstimmungsgrad
  const sortedRecommendations = recommendations.sort((a, b) => b.match - a.match);
  
  // Markiere die beste Empfehlung
  if (sortedRecommendations.length > 0) {
    sortedRecommendations[0].isRecommended = true;
  }

  return sortedRecommendations;
}

// Kriterien-Funktionen

function leibrenteKriterien(formData: FormData): boolean {
  return (
    (formData.age === '76-80' || formData.age === '81+') &&
    formData.priority === 'Sicherheit' &&
    formData.paymentForm === 'Monatliche Rente' &&
    formData.futurePlans === 'Lebenslang in der Immobilie bleiben'
  );
}

function teilverkaufKriterien(formData: FormData): boolean {
  return (
    formData.priority === 'Flexibilität' ||
    formData.monthlyPayments === 'Ja' ||
    (formData.paymentForm === 'Kombination' && formData.estimatedValue !== 'Unter 200k€')
  );
}

function wohnungsrechtKriterien(formData: FormData): boolean {
  return (
    formData.priority === 'Einfachheit' &&
    formData.paymentForm === 'Einmalzahlung' &&
    formData.monthlyPayments === 'Nein'
  );
}

function niessbrauchKriterien(formData: FormData): boolean {
  return (
    formData.futurePlans === 'Eventuell ins Pflegeheim' ||
    formData.futurePlans === 'Zu Familie ziehen' ||
    formData.mainReason === 'Familie unterstützen'
  );
}

function rueckmietKriterien(formData: FormData): boolean {
  return (
    formData.age === '60-65' || 
    formData.age === '66-70' ||
    formData.paymentForm === 'Einmalzahlung' &&
    formData.monthlyPayments === 'Ja'
  );
}

function umkehrhypothekKriterien(formData: FormData): boolean {
  return (
    formData.mainReason === 'Renovierung finanzieren' ||
    (formData.financialSituation === 'Schwierig' && formData.estimatedValue !== 'Unter 200k€')
  );
}

// Empfehlungs-Erstellungs-Funktionen

function createLeibrente(formData: FormData): Recommendation {
  let match = 95;
  
  // Anpassung basierend auf anderen Faktoren
  if (formData.financialSituation === 'Schwierig') match += 5;
  if (formData.familyStatus === 'Single') match += 3;
  
  return {
    id: 'leibrente',
    title: 'Leibrente',
    match: Math.min(match, 100),
    description: 'Perfekt für Sie: Regelmäßige monatliche Zahlungen bis zum Lebensende bei mietfreiem Wohnen in Ihrer Immobilie.',
    advantages: [
      'Lebenslange finanzielle Sicherheit',
      'Mietfreies Wohnen bis zum Lebensende',
      'Planbare monatliche Einnahmen',
      'Keine Instandhaltungskosten mehr',
      'Schutz vor Inflation durch Wertsteigerungsklauseln'
    ],
    disadvantages: [
      'Kaufpreis liegt unter dem Marktwert',
      'Immobilie kann nicht mehr vererbt werden',
      'Bindung an einen Anbieter'
    ],
    nextSteps: 'Wir empfehlen Ihnen ein persönliches Beratungsgespräch zur Leibrente. Unsere Experten berechnen Ihre individuelle Rentenhöhe.'
  };
}

function createTeilverkauf(formData: FormData): Recommendation {
  let match = 85;
  
  if (formData.priority === 'Flexibilität') match += 10;
  if (formData.estimatedValue === 'Über 800k€') match += 5;
  
  return {
    id: 'teilverkauf',
    title: 'Teilverkauf',
    match: Math.min(match, 100),
    description: 'Ideal für flexible Lösungen: Verkaufen Sie nur den Teil Ihrer Immobilie, den Sie wirklich benötigen.',
    advantages: [
      'Flexibler Kapitalbedarf - Sie bestimmen den Anteil',
      'Rückkaufsrecht für verkaufte Anteile möglich',
      'Behalten alle Rechte an der Immobilie',
      'Vermietung und Verkauf weiterhin möglich',
      'Erbschaft bleibt teilweise erhalten'
    ],
    disadvantages: [
      'Monatliche Nutzungsentgelte für verkaufte Anteile',
      'Komplexere Vertragsstruktur',
      'Wertsteigerung wird geteilt'
    ],
    nextSteps: 'Lassen Sie uns gemeinsam berechnen, welcher Verkaufsanteil optimal für Ihre Bedürfnisse ist.'
  };
}

function createWohnungsrecht(formData: FormData): Recommendation {
  let match = 80;
  
  if (formData.priority === 'Einfachheit') match += 10;
  if (formData.monthlyPayments === 'Nein') match += 5;
  
  return {
    id: 'wohnungsrecht',
    title: 'Verkauf mit Wohnungsrecht',
    match: Math.min(match, 100),
    description: 'Einfach und unkompliziert: Erhalten Sie eine Einmalzahlung und wohnen mietfrei bis zum Lebensende.',
    advantages: [
      'Einfache und übersichtliche Struktur',
      'Keine monatlichen Belastungen',
      'Sofortige Liquidität durch Einmalzahlung',
      'Mietfreies Wohnen garantiert',
      'Keine Instandhaltungskosten'
    ],
    disadvantages: [
      'Kaufpreis liegt unter dem vollen Marktwert',
      'Keine Vermietung der Immobilie möglich',
      'Immobilie kann nicht vererbt werden'
    ],
    nextSteps: 'Wir zeigen Ihnen, wie hoch Ihre Einmalzahlung bei einem Verkauf mit Wohnungsrecht sein könnte.'
  };
}

function createNiessbrauch(formData: FormData): Recommendation {
  let match = 75;
  
  if (formData.futurePlans === 'Eventuell ins Pflegeheim') match += 10;
  if (formData.mainReason === 'Familie unterstützen') match += 8;
  
  return {
    id: 'niessbrauch',
    title: 'Nießbrauch',
    match: Math.min(match, 100),
    description: 'Zukunftssicher: Behalten Sie alle Nutzungsrechte und können die Immobilie sogar vermieten, falls Sie ausziehen.',
    advantages: [
      'Vermietung nach Auszug möglich',
      'Umfassende Nutzungsrechte',
      'Flexibilität bei Lebensveränderungen',
      'Steuervorteile bei Schenkung zu Lebzeiten',
      'Erbschaftsplanung optimiert'
    ],
    disadvantages: [
      'Schwieriger zu vermarkten als andere Optionen',
      'Instandhaltung bleibt beim Verkäufer',
      'Kaufpreis oft niedriger'
    ],
    nextSteps: 'Wir finden spezialisierte Investoren für nießbrauchsbelastete Immobilien und optimieren Ihre Steuervorteile.'
  };
}

function createRueckmiete(formData: FormData): Recommendation {
  let match = 70;
  
  if (formData.age === '60-65' || formData.age === '66-70') match += 15;
  if (formData.paymentForm === 'Einmalzahlung') match += 5;
  
  return {
    id: 'rueckmiete',
    title: 'Verkauf mit Rückmiete',
    match: Math.min(match, 100),
    description: 'Auch für jüngere Eigentümer geeignet: Verkaufen Sie Ihre Immobilie und mieten sie direkt zurück.',
    advantages: [
      'Kein Mindestalter erforderlich',
      'Sofortige komplette Liquidität',
      'Mieterrechte und -schutz',
      'Keine Instandhaltungskosten mehr',
      'Planbare monatliche Kosten'
    ],
    disadvantages: [
      'Monatliche Mietzahlungen erforderlich',
      'Nur schuldrechtliche Absicherung',
      'Abhängigkeit vom Vermieter',
      'Mieterhöhungen möglich'
    ],
    nextSteps: 'Wir berechnen Ihre monatliche Miete nach dem Verkauf und finden seriöse Käufer mit Rückmietgarantie.'
  };
}

function createUmkehrhypothek(formData: FormData): Recommendation {
  let match = 65;
  
  if (formData.mainReason === 'Renovierung finanzieren') match += 20;
  if (formData.estimatedValue === 'Über 800k€') match += 10;
  
  return {
    id: 'umkehrhypothek',
    title: 'Umkehrhypothek',
    match: Math.min(match, 100),
    description: 'Flexible Finanzierung: Erhalten Sie einen Kredit gegen Ihre Immobilie ohne monatliche Rückzahlung.',
    advantages: [
      'Flexible Auszahlung nach Bedarf',
      'Keine Tilgung zu Lebzeiten erforderlich',
      'Eigentum bleibt vollständig erhalten',
      'Erben können Kredit übernehmen',
      'Ideal für temporären Liquiditätsbedarf'
    ],
    disadvantages: [
      'Zinsen laufen weiter auf',
      'Begrenzter Kreditrahmen',
      'Immobilie dient als Sicherheit',
      'Nicht bei allen Banken verfügbar'
    ],
    nextSteps: 'Wir prüfen Ihre Bonität und finden spezialisierte Anbieter für Umkehrhypotheken in Ihrer Region.'
  };
}

function createIndividuelleBeratung(): Recommendation {
  return {
    id: 'individuelle-beratung',
    title: 'Individuelle Beratung empfohlen',
    match: 90,
    description: 'Ihre Situation ist einzigartig. Lassen Sie uns gemeinsam die beste Lösung für Sie entwickeln.',
    advantages: [
      'Maßgeschneiderte Lösung für Ihre Situation',
      'Persönliche Betreuung durch Experten',
      'Alle Optionen werden detailliert geprüft',
      'Berücksichtigung aller individuellen Faktoren',
      'Kostenlose und unverbindliche Erstberatung'
    ],
    disadvantages: [
      'Erfordert detaillierte persönliche Analyse',
      'Mehr Zeitaufwand für die Beratung'
    ],
    nextSteps: 'Vereinbaren Sie einen kostenlosen Beratungstermin. Unsere Experten analysieren Ihre Situation im Detail.'
  };
}